import { IAccountingEntry } from "./accounting-entry";
import {
  ICustomFieldAnswer,
  IPackagePrice,
  IProductCategory,
  PCTypes,
  SeatType,
} from "./product-category";
import { ITimeSlot } from "./timeslots";

export enum BookingStatus {
  Pending = 1,
  WaitingList,
  Confirmed,
  Canceled, // used in cancelledAt field
  Abandoned, // unholded
  PendingOrganiser,
  PendingCron,
}

export enum PaymentStatus {
  NotStarted = 1,
  Started,
  WaitingForConfirmation,
  Completed,
  Failed,
  Canceled,
}

export enum RefundStatus {
  None = 1,
  Started,
  WaitingForPspConfirmation,
  Completed,
  Failed,
  WaitingForAccounting,
}

export enum ScanningStatus {
  NotScanned = 0,
  Scanned,
}

export enum PSSType {
  Ticket = 0, // a booking of ticket products
  Retail, // a booking of a retail product
}

// PSS-level check for any seating-plan ticket. pss.type may be:
//   - SeatedSub (31) — fresh PSS or BE-stored sub
//   - Seated (3) — regular seating plan after reset in createPSS
//   - MembershipSeated (17) — membership seating plan after reset (ORD-555 / ORD-557)
export function isSeatedPssType(type: number | null | undefined): boolean {
  return (
    type === PCTypes.SeatedSub ||
    type === PCTypes.Seated ||
    type === PCTypes.MembershipSeated
  );
}

export enum QRCodeTypes {
  Regular = 1,
  HANDSHAKESMART = 2,
  CameParkare = 3, //format XXXXXXXXXXXXXXXXX=YYMMDDST4P9694
}

export enum TicketSeasonalityType {
  TICKET_TYPE_1_DAY = 101,
  TICKET_TYPE_SEASON = 201,
}

export enum PersonCategory {
  PERSON_CATEGORY_ADULT = "010",
  PERSON_CATEGORY_REDUCED = "020",
  PERSON_CATEGORY_CHILD = "030",
  PERSON_CATEGORY_OTHER = "040",
}

export enum PriceCategory {
  CATEGORY_ADULT = 1,
  CATEGORY_REDUCED = 2,
  CATEGORY_CHILD = 3,
  CATEGORY_OTHERS = 4,
}

export interface IQuantifiedProductSlotState {
  pssType: string;
  pcType?: PCTypes;
  uuid: string | null;
  categoryId: number;
  categoryName: string;
  priceId: number;
  priceName: string;
  priceValue: number;
  accountingValue?: number;
  timeSlotId?: number | null; // undefined in case of retail products
  startDateTime?: string; // undefined in case of retail products
  endDateTime?: string; // undefined in case of retail products
  retailOptionId?: number; // undefined in case of ticket products
  optionName?: string; // undefined in case of ticket products
  quantity: number;
  pssIds: string[];
  bookingStatus?: BookingStatus; // Confirmed PSS are going to be grouped separately, because they have different removal logic because they might have a payment
  _discountValue?: number;
  isAutomaticAddon?: boolean;
  mainTicketUUID?: string;
  stockIds?: number[];
  // stock units one ticket consumes on its shared stocks
  stockUnits?: number;
  discountValue?: number;
  hasAutomaticAddons?: boolean;
  isChillonPackage?: boolean;
  orderSessionId?: string;

  // holds a list of seats if this group of pss are seating ticket
  seats?: string[];
}

export interface IProductSlotState {
  _id: string;
  id?: number;
  uuid: string | null;
  token: string | null;
  bookingId?: number | null;
  bookingUuid: string | null;
  orderSessionId: string;
  // extToken: string | null;
  bookingStatus: BookingStatus;
  paymentStatus: PaymentStatus;
  scanningStatus: ScanningStatus;
  refundStatus?: RefundStatus;
  // sendTicket: 0 | 1;
  // needPhoneValidation: 0 | 1;
  creationDateTime: string;
  // createdAtTz: string;
  // updatedAtTz: string;
  canceledAt?: string | null;
  // showCustomFields: 0 | 1;
  refundedAt?: string | null;
  updatedAt: string;
  // overDateTime: string | null;
  scanningUserId: number | null;
  // firstName: string | null;
  // phone: string | null;
  // lastName: string | null;
  email?: string | null;
  // swisspassTicketId: string | null;
  startDateTime?: string;
  endDateTime?: string;
  // startDateTimeTz: string;
  // endDateTimeTz: string;
  priceValue: number;
  accountingValue?: number;
  // onlineValue: number;
  // onsiteCashValue: number;
  // onsiteCardSmeetzValue: number;
  // onsiteCardExtValue: number;
  // onlineLinkValue: number;
  // invitationValue: number;
  priceId: number;
  priceName: string;
  paymentFeeCollected: number;
  paymentFeeEffective: number;
  paymentFeeBilled: number;
  // lausanneTax: number;
  // smeetzFee: number;
  // smeetzChannelFee: number;
  // cancellationFee: number;
  // refundFee: number;
  refundAmount: number;
  barCode?: string;
  categoryId: number;
  categoryName: string;
  productId: number;
  productName?: string;
  productCustomText?: string | null;
  seat?: string | null;
  seatType?: SeatType; // Seat or GeneralAdmissionArea
  eventKey?: string | null;
  fullSeat?: any | null;
  type: PCTypes;
  holdToken?: string | null;
  // info: string | null;
  // googleEventId: string | null;
  // priceBasis: number;
  // orderId: string;
  // ticketSection: string | null;
  promoCodeId?: number | null;
  // voucherId: number | null;
  // membershipTicketId: number | null;
  // membershipId: number | null;
  promocodeDiscountValue?: number;
  // promoCodeSmeetzId: number | null;
  // discountValueSmeetz: number;
  // discountValue: number;
  // bundleDiscountValue: number;
  // bookingStatementId: number | null;
  timeSlotId?: number | null | undefined; // Timeslots are null for refund as voucher, and undefined for retail products
  // slotId: number | null;
  // afterEventBookingStatementId: number | null;
  // cancellationBookingStatementId: number | null;
  // genderId: number | null;
  // dynamicPriceId: number | null;
  // mainTicketId: number | null;
  mainSlot: string | null;
  packagePriceId: number | null;
  // additionalInfoStatus: 1 | 0 | null;
  // depositType: number;
  // strategyPriceId: number | null;
  // attendeeId: number | null;
  // controlGroupUserId: number | null;
  // abTestingGroup: number | null;
  // trackerId: string | null;
  // isPrinted: 1 | 0;
  // isEmailed: 1 | 0;
  sellerUserId: number | null;
  // sellerGroupId: number | null;
  productOwnerGroupId: number | null;
  _discountValue?: number;
  // Differentiate merch product pss from ticket pss
  pssType: string;
  retailOptionId?: number; // retail id are null in case of a ticket product
  retailOptionName?: string; // retail name are null in case of a ticket product
  sendTicket: number | boolean;
  isAutomaticAddon?: boolean;
  mainTicketUUID?: string;
  customFieldsAnswers?: ICustomFieldAnswer[];
  hasAutomaticAddons?: boolean;
  stockIds?: number[];
  // stock units this ticket consumes on its shared stocks
  stockUnits?: number;
  isMemberhsipSeated?: boolean;
  isChillonPackage?: boolean;
  appliedMembershipToken?: string;
}
export interface IPSSBalance {
  pssUuid: string | null;
  balance: number;
  pss: IProductSlotState;
  accEntries: IAccountingEntry[];
}

export enum CodeTransactionType {
  Payment = 1,
  Discount = 2,
}

export interface ICreateAddons {
  productCategory: IProductCategory;
  priceId: number;
  count: number;
  timeslot?: ITimeSlot;
  packagePriceId: number | null;
  sendTicket: number | boolean;
  automaticallyBook: IPackagePrice["automaticallyBook"];
  parentSeatingType?: PCTypes; // type of the seating-plan parent PC (Seated=3 vs MembershipSeated=17), drives pss.type for membership seating plan
}
